// Keeps the bloom off the Sun's own disc. UnrealBloomPass adds the frame's
// blurred bright light back over everything: round the Sun that is the
// glow Andre approved, but on the disc it is the disc's own light laid over
// itself, which fills the sunspots (a big umbra read at 0.65 of the
// photosphere instead of about 0.2, where SDO/HMI shows it). This pass runs
// right after the bloom and takes the bloom's own image back off the
// pixels whose view ray hits the photosphere sphere, so the glow, the limb
// and the prominences outside the disc are exactly as before and the disc
// shows its surface as rendered. It eases in as the disc grows from a few
// degrees across, so a distant Sun (a bright point) keeps its bloom.
//
//   const pass = createSunDiscBloomPass(bloomPass); composer.addPass(pass);
//   updateSunDiscBloom(pass, { camera, sun, enabled })   // per frame, sun the Sun's group
//
// window.__sunDiscBloom = false turns it off for before/after captures.

import * as THREE from 'three';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';

const SunDiscBloomShader = {
  uniforms: {
    tDiffuse: { value: null },
    tBloom: { value: null },
    uAmount: { value: 0 },
    uSunView: { value: new THREE.Vector3(0, 0, -10) },
    uSunRadius: { value: 1 },
    uInvProj: { value: new THREE.Matrix4() },
    uFeather: { value: 0.001 },
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform sampler2D tBloom;
    uniform float uAmount;
    uniform vec3 uSunView;
    uniform float uSunRadius;
    uniform mat4 uInvProj;
    uniform float uFeather;
    varying vec2 vUv;

    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      if (uAmount > 0.0) {
        // The pixel's view ray against the photosphere sphere (view space):
        // the ray's closest approach to the Sun's centre, in its radii.
        // On the near plane: the far plane is too far for float precision.
        vec4 p = uInvProj * vec4(vUv * 2.0 - 1.0, -1.0, 1.0);
        vec3 d = normalize(p.xyz / p.w);
        float along = dot(uSunView, d);
        float miss = sqrt(max(dot(uSunView, uSunView) - along * along, 0.0)) / uSunRadius;
        // The share of the pixel the disc covers: the limb's own
        // antialiasing, a pixel wide and centred on it, so the edge pixel
        // loses only the disc's part of the bloom (no bright or dark rim).
        float disc = along > 0.0 ? 1.0 - smoothstep(1.0 - 0.5 * uFeather, 1.0 + 0.5 * uFeather, miss) : 0.0;
        // The bloom was blended additively with its own alpha.
        vec4 b = texture2D(tBloom, vUv);
        color.rgb = max(color.rgb - disc * uAmount * b.rgb * b.a, vec3(0.0));
      }
      gl_FragColor = color;
    }
  `,
};

export function createSunDiscBloomPass(bloomPass) {
  const pass = new ShaderPass(SunDiscBloomShader);
  pass.userData = { bloomPass };
  return pass;
}

const _c = new THREE.Vector3();
const _s = new THREE.Vector3();

// The disc's half-angle (radians) where it starts and ends taking the bloom
// off: a few degrees across to about 18.
const FROM = 0.03;
const TO = 0.16;

export function updateSunDiscBloom(pass, { camera, sun, enabled = true }) {
  if (!pass) return;
  const u = pass.uniforms;
  const bloom = pass.userData.bloomPass;
  const off = typeof window !== 'undefined' && window.__sunDiscBloom === false;
  const sunMesh = sun && sun.children.find((c) => c.isMesh && c.material && c.material.uniforms && c.material.uniforms.realisticSun);
  if (!enabled || off || !camera || !sunMesh || !bloom || !bloom.renderTargetsHorizontal) { u.uAmount.value = 0; return; }
  camera.updateMatrixWorld();
  sunMesh.getWorldPosition(_c);
  const R = sunMesh.geometry.parameters.radius * sunMesh.getWorldScale(_s).x;
  _c.applyMatrix4(camera.matrixWorldInverse);
  const dist = _c.length();
  const half = Math.asin(Math.min(1, R / Math.max(dist, 1e-9)));
  u.uAmount.value = _c.z < 0 ? THREE.MathUtils.smoothstep(half, FROM, TO) : 0;
  if (u.uAmount.value <= 0) return;
  u.tBloom.value = bloom.renderTargetsHorizontal[0].texture;
  u.uSunView.value.copy(_c);
  u.uSunRadius.value = R;
  u.uInvProj.value.copy(camera.projectionMatrixInverse);
  // A pixel's angle, as a fraction of the disc's radius at its distance.
  const heightPx = Math.max(1, 2 * bloom.renderTargetsHorizontal[0].height);
  const pixelAngle = (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov || 45) * 0.5)) / Math.max(camera.zoom || 1, 1e-3) / heightPx;
  u.uFeather.value = Math.min(0.05, (pixelAngle * dist) / R);
}
